import { useEffect, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../cn';

export function Card({ children, className, onClick, accent }: { children: ReactNode; className?: string; onClick?: () => void; accent?: string }) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp {...(onClick ? { onClick, type: 'button' as const } : {})} className={cn('block w-full rounded-card bg-white p-4 text-left shadow-card', onClick && 'active:bg-neutral-50', className)} style={accent ? { borderLeft: `4px solid ${accent}` } : undefined}>
      {children}
    </Comp>
  );
}

export function Section({ title, action, children, className }: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('px-4 py-2', className)}>
      {(title || action) && (
        <div className="mb-2 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-ink-3"><span aria-hidden className="h-3.5 w-1 rounded-full bg-cat" />{title}</h2>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function ListRow({ leading, title, subtitle, trailing, onClick, chevron, className, danger }: { leading?: ReactNode; title: ReactNode; subtitle?: ReactNode; trailing?: ReactNode; onClick?: () => void; chevron?: boolean; className?: string; danger?: boolean }) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp {...(onClick ? { onClick, type: 'button' as const } : {})} className={cn('flex w-full items-center gap-3 px-4 py-3 text-left', onClick && 'active:bg-neutral-50', className)}>
      {leading && <span className="shrink-0">{leading}</span>}
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate text-[15px] font-medium', danger ? 'text-bad' : 'text-ink')}>{title}</span>
        {subtitle && <span className="block truncate text-[13px] text-ink-3">{subtitle}</span>}
      </span>
      {trailing && <span className="shrink-0 text-right">{trailing}</span>}
      {chevron && <ChevronRight size={18} className="shrink-0 text-neutral-400" />}
    </Comp>
  );
}

export function List({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('divide-y divide-line overflow-hidden rounded-card bg-white shadow-card', className)}>{children}</div>;
}

/**
 * A number tile. The value shrinks to fit its tile (container-query units), so ৳১২,৩৪,৫৬৭ in a three-column row on a phone
 * stays inside the card instead of running over the next one; as a last resort a very long value wraps.
 */
export function Stat({ label, value, tone, sub, className }: { label: ReactNode; value: ReactNode; tone?: 'ok' | 'bad' | 'brand' | 'warn' | 'cat'; sub?: ReactNode; className?: string }) {
  const c = tone === 'cat' ? 'text-cat-deep' : tone === 'ok' ? 'text-ok' : tone === 'bad' ? 'text-bad' : tone === 'brand' ? 'text-brand' : tone === 'warn' ? 'text-warn' : 'text-ink';
  const len = typeof value === 'string' || typeof value === 'number' ? [...String(value)].length : 0;
  // ~0.6em per tabular digit: the font size that makes `len` characters fill the tile's width, between 0.8rem and 1.25rem
  const fit = len > 7 ? { fontSize: `clamp(0.8rem, ${(165 / len).toFixed(1)}cqw, 1.25rem)` } : undefined;
  return (
    <div className={cn('min-w-0 rounded-2xl p-3.5 shadow-card [container-type:inline-size]', tone === 'cat' ? 'bg-gradient-to-br from-cat-soft to-white ring-1 ring-cat/15' : 'bg-white', className)}>
      <div className="text-xs font-medium leading-snug text-ink-3">{label}</div>
      <div className={cn('tabular mt-0.5 text-xl font-extrabold leading-tight [overflow-wrap:anywhere]', c)} style={fit}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-ink-3">{sub}</div>}
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <div className={cn('h-px bg-line', className)} />;
}

export function ProgressBar({ value, className, color }: { value: number; className?: string; color?: string }) {
  return (
    <div className={cn('h-2 overflow-hidden rounded-full bg-neutral-200', className)}>
      <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }} />
    </div>
  );
}

/** Round avatar: the photo when there is one (v10 — customers, the owner), else the first letter on a colour. A broken image falls back to the letter. */
export function Avatar({ name, size = 40, color = '#D12B29', src }: { name: string; size?: number; color?: string; src?: string | null }) {
  const ch = [...(name.trim() || '?')][0]!.toUpperCase();
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  if (src && !broken) {
    return <img src={src} alt={name} loading="lazy" decoding="async" onError={() => setBroken(true)} className="shrink-0 rounded-full bg-neutral-100 object-cover" style={{ width: size, height: size }} />;
  }
  return (
    <span className="inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white" style={{ width: size, height: size, background: color, fontSize: size * 0.42 }}>
      {ch}
    </span>
  );
}

/** Round icon chip in the shop's category colour: for list leading icons and quick actions (icon on a light tint). */
export function CatChip({ children, size = 40, className }: { children: ReactNode; size?: number; className?: string }) {
  return <span className={cn('flex shrink-0 items-center justify-center rounded-xl bg-cat-soft text-cat-deep', className)} style={{ width: size, height: size }}>{children}</span>;
}

/** Highlight card: category-tinted, with a category-coloured edge. Used for the one number or message that matters on a screen. */
export function CatCard({ children, className, onClick }: { children: ReactNode; className?: string; onClick?: () => void }) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp {...(onClick ? { onClick, type: 'button' as const } : {})} className={cn('block w-full rounded-card bg-gradient-to-br from-cat-soft to-white p-4 text-left shadow-card ring-1 ring-cat/15', onClick && 'active:brightness-95', className)}>
      {children}
    </Comp>
  );
}
